import { Segmented } from "@somethings-ui/web";
import { Calendar, List } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useHubStore } from "@/components/hub/use-hub-store";
import type { ActionHistoryView } from "../action-analytics";
import type { UseActionAnalyticsResult } from "../use-action-analytics";
import { HistoryCalendarView } from "./history-calendar-view";
import { HistoryListView } from "./history-list-view";
import {
  HISTORY_DAY_TIMEZONE_FALLBACK,
  hubViewForHistoryEntry,
  type HistoryEntry,
} from "./history-model";
import { useHistoryEntries } from "./use-history-entries";

const VIEW_OPTIONS = [
  { value: "list", label: "All", icon: <List className="size-3.5" /> },
  {
    value: "calendar",
    label: "Calendar",
    icon: <Calendar className="size-3.5" />,
  },
];

interface HistoryTabProps {
  menteeId: string | null;
  mentorshipId: string | null;
  /** Mentor IANA timezone; falls back to America/New_York. */
  timezone?: string | null;
  analytics: UseActionAnalyticsResult;
}

export function HistoryTab({
  menteeId,
  mentorshipId,
  timezone,
  analytics,
}: HistoryTabProps) {
  const [view, setView] = useState<ActionHistoryView>("list");
  const { trackHistoryView, trackHistoryEntrySelect } = analytics;
  const openToView = useHubStore((s) => s.openToView);
  const historyTimezone = timezone ?? HISTORY_DAY_TIMEZONE_FALLBACK;

  const {
    entries,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useHistoryEntries(menteeId);

  useEffect(() => {
    trackHistoryView(view);
  }, [trackHistoryView, view]);

  const handleViewChange = useCallback(
    (next: string) => setView(next === "calendar" ? "calendar" : "list"),
    [],
  );

  const handleSelectEntry = useCallback(
    (entry: HistoryEntry) => {
      const toolId = entry.activityToolId ?? entry.contentToolId ?? "";
      trackHistoryEntrySelect(entry.group, toolId);
      const hubView = hubViewForHistoryEntry(entry);
      if (hubView) openToView(hubView, entry.id);
    },
    [openToView, trackHistoryEntrySelect],
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center px-4 py-3">
        <Segmented
          options={VIEW_OPTIONS}
          value={view}
          onChange={handleViewChange}
          aria-label="History view"
        />
      </div>

      {view === "list" ? (
        <HistoryListView
          entries={entries}
          timezone={historyTimezone}
          isLoading={isLoading}
          isError={isError}
          refetch={refetch}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onSelectEntry={handleSelectEntry}
        />
      ) : (
        <HistoryCalendarView
          mentorshipId={mentorshipId}
          analytics={analytics}
          onSelectEntry={handleSelectEntry}
        />
      )}
    </div>
  );
}
